import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Users, IndianRupee, CheckCircle2, Clock, AlertTriangle, MoreVertical,
  Download, Search, ImagePlus, Trash2, FileText, Calendar, Plus, Upload, Pencil, KeyRound, ShieldAlert, UserCheck,
} from "lucide-react";
import { formatDateDMY } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import { canManageContent, canManageFinance, isAdminRole, isSuperAdmin, ROLE_LABELS } from "@/lib/roles";
import FileViewer from "@/components/FileViewer";
import { Receipt, IndianRupee as IndianRupeeIcon, Eye, UserPlus } from "lucide-react";
import CreateMemberDialog from "@/components/CreateMemberDialog";
import EditMemberDialog from "@/components/EditMemberDialog";
import { downloadReceiptPDF, downloadIDCardPDF } from "@/lib/pdf";
import { downloadCSV, downloadXLSX, downloadTablePDF, sortRows, toggleSort } from "@/lib/tableUtils";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { DISTRICTS } from "@/data/static";
import SecurityTab from "@/components/SecurityTab";
import SettingsTab from "@/components/SettingsTab";
import CharterUsageTab from "@/components/charter/CharterUsageTab";
import CharterStudioTab from "@/components/charter/CharterStudioTab";
import AdminInsuranceTab from "@/components/insurance/AdminInsuranceTab";
import TakePaymentDialog from "@/components/TakePaymentDialog";
import MemberOverviewCard from "@/components/MemberOverviewCard";
import DistrictMembersDialog, { districtMemberRows } from "@/components/DistrictMembersDialog";
import ConferenceAdminPanel from "@/components/conference/ConferenceAdminPanel";
import { payWithRazorpay } from "@/lib/razorpay";

export default function AdminDashboard() {
  const { user } = useAuth();
  const role = user?.role;
  const allowContent = canManageContent(role);
  const allowFinance = canManageFinance(role);
  const allowSuper = isSuperAdmin(role);
  const defaultTab = allowContent ? "members" : (allowFinance ? "payments" : "districts");

  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [q, setQ] = useState("");
  const [memberSort, setMemberSort] = useState({ key: null, dir: null });
  const [createMemberOpen, setCreateMemberOpen] = useState(false);
  const [editMemberTarget, setEditMemberTarget] = useState(null);
  const [paymentSort, setPaymentSort] = useState({ key: null, dir: null });
  const [districtDetail, setDistrictDetail] = useState(null);

  const load = () => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/admin/members" + (filterStatus !== "all" ? `?status=${filterStatus}` : "")).then(r => setMembers(r.data)).catch(() => {});
    api.get("/admin/payments").then(r => setPayments(r.data)).catch(() => {});
    api.get("/tickets").then(r => setTickets(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filterStatus]);

  const action = async (uid, kind) => {
    try {
      await api.post(`/admin/members/${uid}/action`, { action: kind });
      toast.success(`Member ${kind}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const goMembers = (status) => {
    setFilterStatus(status);
    setTab("members");
    setTimeout(() => document.querySelector('[data-testid="admin-member-search"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [takePaymentTarget, setTakePaymentTarget] = useState(null); // { member, mode }
  const [payingSelf, setPayingSelf] = useState(false);
  const [tab, setTab] = useState(defaultTab);
  const [twofaEnabled, setTwofaEnabled] = useState(null);
  useEffect(() => {
    if (allowSuper) {
      api.get("/auth/2fa/status")
        .then(r => setTwofaEnabled(!!r.data.enabled))
        .catch(() => setTwofaEnabled(null));
    }
  }, [allowSuper, tab]);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/members/${deleteTarget.id}`);
      toast.success(`Member "${deleteTarget.full_name}" deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const searched = members.filter(m => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (m.full_name || "").toLowerCase().includes(s)
      || (m.email || "").toLowerCase().includes(s)
      || (m.membership_id || "").toLowerCase().includes(s);
  });
  const memberKeyMap = {
    full_name: m => m.full_name || "",
    membership_id: m => m.membership_id || "",
    district: m => m.district || "",
    local_body_name: m => m.local_body_name || "",
    payment_status: m => m.payment_status || "",
    membership_status: m => m.membership_status || "",
  };
  const filtered = sortRows(searched, memberSort, memberKeyMap);

  const sortedPayments = sortRows(payments, paymentSort, {
    receipt_no: p => p.receipt_no || "",
    member_name: p => p.member_name || "",
    purpose: p => p.purpose || "",
    method: p => p.method || "",
    status: p => p.status || "",
    date: p => new Date(p.date).getTime(),
    amount: p => Number(p.amount) || 0,
  });

  const memberRows = () => {
    const header = ["Membership ID", "Name", "Email", "Mobile", "District", "Local Body", "Local Body Type", "Payment", "Status"];
    const body = filtered.map(m => [
      m.membership_id || "", m.full_name || "", m.email || "", m.mobile || "",
      m.district || "", m.local_body_name || "", m.local_body_type || "",
      m.payment_status || "", m.membership_status || "",
    ]);
    return [header, ...body];
  };
  const paymentRows = () => {
    const header = ["Receipt No.", "Transaction ID", "Member", "Membership ID", "Purpose", "Method", "Amount (INR)", "Status", "Date"];
    const body = sortedPayments.map(p => [
      p.receipt_no || "", p.transaction_id || "", p.member_name || "", p.membership_id || "",
      p.purpose || "", p.method || "", Number(p.amount) || 0, p.status || "",
      p.date ? new Date(p.date).toLocaleString("en-IN") : "",
    ]);
    return [header, ...body];
  };

  const exportMembersCSV = () => downloadCSV(memberRows(), "kelta_members.csv");
  const exportMembersXLSX = () => downloadXLSX(memberRows(), "kelta_members.xlsx", "Members");
  const exportMembersPDF = () => downloadTablePDF(memberRows(), "kelta_members.pdf", { title: "KELTA — Members" });
  const exportPaymentsCSV = () => downloadCSV(paymentRows(), "kelta_payments.csv");
  const exportPaymentsXLSX = () => downloadXLSX(paymentRows(), "kelta_payments.xlsx", "Payments");
  const exportPaymentsPDF = () => downloadTablePDF(paymentRows(), "kelta_payments.pdf", { title: "KELTA — Payments" });

  const districtRows = (s) => {
    const map = (s && (s.by_district_paid_active || s.by_district)) || {};
    const header = ["District", "Paid & Active Members"];
    const body = Object.entries(map).map(([district, count]) => [district, Number(count) || 0]);
    const total = body.reduce((a, r) => a + Number(r[1] || 0), 0);
    body.push(["Total", total]);
    return [header, ...body];
  };
  const exportDistrictsCSV = (s) => downloadCSV(districtRows(s), "kelta_districts_paid_active.csv");
  const exportDistrictsXLSX = (s) => downloadXLSX(districtRows(s), "kelta_districts_paid_active.xlsx", "Districts");
  const exportDistrictsPDF = (s) => downloadTablePDF(districtRows(s), "kelta_districts_paid_active.pdf", {
    title: "KELTA — Paid & Active Members by District",
    subtitle: "Kerala Local Body Technical Assistants Association",
    brandColor: [0, 94, 184],           // KELTA blue
    accentColor: [255, 152, 0],         // KELTA orange
  });

  const exportDistrictDetails = async (kind) => {
    toast.loading("Preparing detailed member export…", { id: "district-detail-export" });
    try {
      const { data } = await api.get("/admin/members/paid-active");
      const rows = districtMemberRows(data.rows || [], true);
      const base = "kelta_all_districts_paid_active_members";
      const title = "KELTA — All District Paid & Active Members";
      if (kind === "csv") downloadCSV(rows, `${base}.csv`);
      if (kind === "xlsx") downloadXLSX(rows, `${base}.xlsx`, "Paid Active Members");
      if (kind === "pdf") downloadTablePDF(rows, `${base}.pdf`, {
        title,
        subtitle: `${data.total || (data.rows || []).length} members across all districts`,
        brandColor: [0, 94, 184],
        accentColor: [255, 152, 0],
      });
      toast.success(`Detailed ${kind.toUpperCase()} exported`, { id: "district-detail-export" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Detailed export failed", { id: "district-detail-export" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">
                Admin Console · {ROLE_LABELS[role] || "Administrator"}
              </div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight">KELTA Administration</h1>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setChangePwOpen(true)}
              data-testid="admin-change-password-btn"
              className="mt-2"
            >
              <KeyRound className="w-4 h-4 mr-1.5" /> Change Password
            </Button>
          </div>

          {/* Your Membership card — for admins to see themselves as a member */}
          <MemberOverviewCard user={user} />

          {/* KPIs — status cards jump to the filtered Members list (with Edit Details) */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <KPI icon={Users} label="Total Members" value={stats?.total ?? 0} color="text-brand-blue" onClick={() => goMembers("all")} testId="stat-card-total" />
            <KPI icon={Clock} label="Pending Review" value={stats?.pending ?? 0} color="text-yellow-600" onClick={() => goMembers("pending_review")} testId="stat-card-pending" />
            <KPI icon={CheckCircle2} label="Active" value={stats?.approved ?? 0} color="text-brand-green" onClick={() => goMembers("active")} testId="stat-card-active" />
            <KPI icon={AlertTriangle} label="Rejected/Suspended" value={(stats?.rejected ?? 0) + (stats?.suspended ?? 0)} color="text-red-500" />
            <KPI icon={IndianRupee} label="Revenue" value={`₹${(stats?.revenue ?? 0).toLocaleString("en-IN")}`} color="text-brand-orange" />
          </div>

          {allowSuper && twofaEnabled === false && (
            <div className="mt-5 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 flex items-start justify-between gap-4" data-testid="admin-2fa-mandatory-banner">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
                <div>
                  <div className="font-semibold text-sm">Two-factor authentication is required for the super admin</div>
                  <p className="text-xs text-muted-foreground mt-0.5">Protect finance data with a 6-digit code from Google Authenticator or Authy. Set it up now — it only takes a minute.</p>
                </div>
              </div>
              <Button
                onClick={() => setTab("security")}
                className="bg-amber-500 hover:bg-amber-600 text-white h-8"
                size="sm"
                data-testid="admin-2fa-mandatory-cta"
              >
                Set up now
              </Button>
            </div>
          )}

          {user && user.payment_status !== "paid" && (
            <div className="mt-5 rounded-lg border border-brand-blue/50 bg-brand-blue/5 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="admin-pay-membership-banner">              <div className="flex items-start gap-3">
                <IndianRupee className="w-5 h-5 mt-0.5 text-brand-blue shrink-0" />
                <div>
                  <div className="font-semibold text-sm">Complete your own KELTA membership</div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    As {ROLE_LABELS[user.role] || "an admin"}, you also need to pay the annual membership fee (₹1,300) to hold membership. Pay online now or visit your <Link to="/dashboard" className="text-brand-blue font-medium underline">Member Dashboard</Link>.
                  </p>
                </div>
              </div>
              <Button
                onClick={async () => {
                  if (payingSelf) return;
                  setPayingSelf(true);
                  try {
                    await payWithRazorpay({
                      amount: 1300,
                      purpose: "membership",
                      prefill: { name: user.full_name || "", email: user.email || "", contact: user.mobile || "" },
                    });
                    toast.success("Payment successful — reload to see updated status");
                    setTimeout(() => window.location.reload(), 1500);
                  } catch (e) {
                    if (e?.message !== "PAYMENT_CANCELLED") toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
                  } finally {
                    setPayingSelf(false);
                  }
                }}
                disabled={payingSelf}
                className="bg-brand-blue hover:bg-brand-blue/90 text-white h-9"
                size="sm"
                data-testid="admin-pay-membership-btn"
              >
                {payingSelf ? "Opening…" : "Pay Membership Fee"}
              </Button>
            </div>
          )}

          {user && user.payment_status === "paid" && (() => {
            const missing = [];
            if (!user.mobile) missing.push("Mobile");
            if (!user.dob) missing.push("Date of Birth");
            if (!user.blood_group) missing.push("Blood Group");
            if (!user.educational_qualification) missing.push("Educational Qualification");
            if (!user.employment_type) missing.push("Employment Type");
            if (!user.date_of_joining) missing.push("Date of Joining");
            if (!user.employee_id) missing.push("Employee ID");
            if (!missing.length) return null;
            return (
              <div className="mt-5 rounded-lg border border-orange-500/50 bg-orange-500/10 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="admin-complete-profile-banner">
                <div className="flex items-start gap-3">
                  <UserCheck className="w-5 h-5 mt-0.5 text-orange-600 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">Complete your KELTA membership profile</div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Please fill in the details you missed at Join Membership: <span className="font-medium text-foreground">{missing.join(", ")}</span>. These are required for your official membership certificate.
                    </p>
                  </div>
                </div>
                <Link to="/register">
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white h-9" data-testid="admin-complete-profile-cta">
                    Complete Membership Form
                  </Button>
                </Link>
              </div>
            );
          })()}

          <Tabs value={tab} onValueChange={setTab} className="mt-8">
            <TabsList>
              {allowContent && <TabsTrigger value="members" data-testid="admin-tab-members">Members</TabsTrigger>}
              {allowFinance && <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>}
              {allowFinance && <TabsTrigger value="claims" data-testid="admin-tab-claims">Claims</TabsTrigger>}
              {allowContent && <TabsTrigger value="tickets" data-testid="admin-tab-tickets">Tickets</TabsTrigger>}
              {allowContent && <TabsTrigger value="circulars" data-testid="admin-tab-circulars">Circulars</TabsTrigger>}
              {allowContent && <TabsTrigger value="events" data-testid="admin-tab-events">Events</TabsTrigger>}
              {allowContent && <TabsTrigger value="gallery" data-testid="admin-tab-gallery">Gallery</TabsTrigger>}
              {allowSuper && <TabsTrigger value="staff" data-testid="admin-tab-staff">Staff</TabsTrigger>}
              {allowSuper && <TabsTrigger value="feedback" data-testid="admin-tab-feedback">Feedback</TabsTrigger>}
              {allowSuper && <TabsTrigger value="security" data-testid="admin-tab-security">Security</TabsTrigger>}
              {allowSuper && <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>}
              {allowSuper && <TabsTrigger value="charter-studio" data-testid="admin-tab-charter-studio">Charter Studio</TabsTrigger>}
              {isAdminRole(role) && <TabsTrigger value="insurance" data-testid="admin-tab-insurance">Group Insurance</TabsTrigger>}
              <TabsTrigger value="charters" data-testid="admin-tab-charters">Charters</TabsTrigger>
              <TabsTrigger value="districts" data-testid="admin-tab-districts">By District</TabsTrigger>
              <TabsTrigger value="conference" data-testid="admin-tab-conference">State Conference</TabsTrigger>
            </TabsList>

            <TabsContent value="members" className="mt-5">
              <CreateMemberDialog
                open={createMemberOpen}
                onClose={() => setCreateMemberOpen(false)}
                onCreated={() => load()}
              />
              <EditMemberDialog
                member={editMemberTarget}
                onClose={() => setEditMemberTarget(null)}
                onSaved={() => load()}
              />
              <Card>
                <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between border-b border-border">
                  <div className="flex flex-1 gap-2 items-center">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute w-4 h-4 left-2.5 top-3 text-muted-foreground" />
                      <Input className="pl-8" placeholder="Search by name / email / ID" value={q} onChange={e => setQ(e.target.value)} data-testid="admin-member-search" />
                    </div>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="pending_payment">Pending Payment</SelectItem>
                        <SelectItem value="pending_review">Pending Review</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setCreateMemberOpen(true)} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="admin-create-member-btn">
                      <UserPlus className="w-4 h-4 mr-1.5" /> Create Member
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportMembersCSV} data-testid="admin-export-btn">
                      <Download className="w-4 h-4 mr-1.5" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportMembersXLSX} data-testid="admin-members-export-xlsx">
                      <Download className="w-4 h-4 mr-1.5" /> Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportMembersPDF} data-testid="admin-members-export-pdf">
                      <Download className="w-4 h-4 mr-1.5" /> PDF
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto scrollbar-thin">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableHead label="Member" sortKey="full_name" state={memberSort} onSort={setMemberSort} testid="sort-member-name" />
                        <SortableHead label="Membership ID" sortKey="membership_id" state={memberSort} onSort={setMemberSort} testid="sort-member-id" />
                        <SortableHead label="District" sortKey="district" state={memberSort} onSort={setMemberSort} testid="sort-member-district" />
                        <SortableHead label="Local Body" sortKey="local_body_name" state={memberSort} onSort={setMemberSort} testid="sort-member-lb" />
                        <SortableHead label="Payment" sortKey="payment_status" state={memberSort} onSort={setMemberSort} testid="sort-member-payment" />
                        <SortableHead label="Status" sortKey="membership_status" state={memberSort} onSort={setMemberSort} testid="sort-member-status" />
                        <TableHead className="w-12" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map(m => (
                        <TableRow key={m.id}>
                          <TableCell>
                            <div className="font-semibold">{m.full_name}</div>
                            <div className="text-xs text-muted-foreground">{m.email}</div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{m.membership_id || "—"}</TableCell>
                          <TableCell>{m.district}</TableCell>
                          <TableCell>
                            <div className="text-sm">{m.local_body_name}</div>
                            <div className="text-xs text-muted-foreground">{m.local_body_type}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={m.payment_status === "paid" ? "default" : "secondary"}>
                              {m.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <StatusBadge s={m.membership_status} />
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`member-actions-${m.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {allowSuper && (
                                  <DropdownMenuItem data-testid={`admin-edit-member-${m.id}`} onClick={() => setEditMemberTarget(m)}>
                                    <Pencil className="w-4 h-4 mr-2" /> Edit Details
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => action(m.id, "approve")}>Approve</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => action(m.id, "reject")}>Reject</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => action(m.id, "suspend")}>Suspend</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => action(m.id, "activate")}>Activate</DropdownMenuItem>
                                {m.payment_status !== "paid" && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      data-testid={`admin-record-offline-${m.id}`}
                                      onClick={() => setTakePaymentTarget({ member: m, mode: "offline" })}
                                    >
                                      <IndianRupee className="w-4 h-4 mr-2" /> Record Offline Payment
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      data-testid={`admin-take-online-${m.id}`}
                                      onClick={() => setTakePaymentTarget({ member: m, mode: "online" })}
                                    >
                                      <IndianRupee className="w-4 h-4 mr-2" /> Take Online Payment
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {m.membership_id && (
                                  <DropdownMenuItem
                                    data-testid={`admin-download-id-${m.id}`}
                                    onClick={async () => {
                                      try {
                                        toast.loading("Generating ID card PDF…", { id: `aid-${m.id}` });
                                        await downloadIDCardPDF(m);
                                        toast.success("ID card downloaded", { id: `aid-${m.id}` });
                                      } catch {
                                        toast.error("Could not generate PDF", { id: `aid-${m.id}` });
                                      }
                                    }}
                                  >
                                    Download ID Card (A4 PDF)
                                  </DropdownMenuItem>
                                )}
                                {allowSuper && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      data-testid={`admin-delete-member-${m.id}`}
                                      disabled={m.payment_status === "paid" || m.membership_status === "active" || !!m.membership_id}
                                      onClick={() => setDeleteTarget(m)}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" /> Delete Member (unpaid only)
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filtered.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-12">No members found.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="payments" className="mt-5">
              <Card>
                <div className="p-4 flex flex-wrap gap-2 border-b border-border justify-between items-center">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" /> Success</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500" /> Created / Pending</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-400" /> Abandoned</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500" /> Failed</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm("Mark all Razorpay 'created' orders older than 24h as 'abandoned'?")) return;
                        try {
                          const { data } = await api.post("/admin/payments/cleanup-stale");
                          toast.success(`Cleaned up ${data.marked_abandoned} stale order(s)`);
                          load();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Cleanup failed");
                        }
                      }}
                      data-testid="admin-payments-cleanup-stale"
                    >
                      Cleanup Stale Orders
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportPaymentsCSV} data-testid="admin-payments-export-csv">
                      <Download className="w-4 h-4 mr-1.5" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportPaymentsXLSX} data-testid="admin-payments-export-xlsx">
                      <Download className="w-4 h-4 mr-1.5" /> Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportPaymentsPDF} data-testid="admin-payments-export-pdf">
                      <Download className="w-4 h-4 mr-1.5" /> PDF
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHead label="Receipt" sortKey="receipt_no" state={paymentSort} onSort={setPaymentSort} testid="sort-payment-receipt" />
                      <SortableHead label="Member" sortKey="member_name" state={paymentSort} onSort={setPaymentSort} testid="sort-payment-member" />
                      <SortableHead label="Purpose" sortKey="purpose" state={paymentSort} onSort={setPaymentSort} testid="sort-payment-purpose" />
                      <SortableHead label="Method" sortKey="method" state={paymentSort} onSort={setPaymentSort} testid="sort-payment-method" />
                      <SortableHead label="Status" sortKey="status" state={paymentSort} onSort={setPaymentSort} testid="sort-payment-status" />
                      <SortableHead label="Date" sortKey="date" state={paymentSort} onSort={setPaymentSort} testid="sort-payment-date" />
                      <SortableHead label="Amount" sortKey="amount" state={paymentSort} onSort={setPaymentSort} align="right" testid="sort-payment-amount" />
                      <TableHead className="w-[110px] text-right">Receipt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPayments.map(p => {
                      const s = (p.status || "").toLowerCase();
                      const rowCls =
                        s === "success" ? "" :
                        s === "created" || s === "pending" ? "bg-amber-50 dark:bg-amber-950/30" :
                        s === "abandoned" ? "bg-slate-50 dark:bg-slate-900/40 text-muted-foreground" :
                        (s === "failed" || s === "signature_mismatch") ? "bg-rose-50 dark:bg-rose-950/30" : "";
                      const badge =
                        s === "success" ? { cls: "bg-emerald-500 hover:bg-emerald-500 text-white", label: "success" } :
                        s === "created" ? { cls: "bg-amber-500 hover:bg-amber-500 text-white", label: "created" } :
                        s === "pending" ? { cls: "bg-amber-500 hover:bg-amber-500 text-white", label: "pending" } :
                        s === "abandoned" ? { cls: "bg-slate-400 hover:bg-slate-400 text-white", label: "abandoned" } :
                        (s === "failed" || s === "signature_mismatch") ? { cls: "bg-rose-500 hover:bg-rose-500 text-white", label: s } :
                        { cls: "bg-muted text-foreground", label: p.status || "—" };
                      const canDownload = s === "success";
                      return (
                        <TableRow key={p.id} className={rowCls} data-testid={`payment-row-${p.id}`} data-status={s}>
                          <TableCell className="font-mono text-xs">{p.receipt_no}</TableCell>
                          <TableCell>{p.member_name || "—"}</TableCell>
                          <TableCell className="capitalize">{p.purpose}</TableCell>
                          <TableCell>{p.method}</TableCell>
                          <TableCell>
                            <Badge className={badge.cls} data-testid={`payment-status-${p.id}`}>{badge.label}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{new Date(p.date).toLocaleString()}</TableCell>
                          <TableCell className="text-right font-mono font-bold">₹{p.amount}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              disabled={!canDownload}
                              data-testid={`admin-receipt-pdf-${p.id}`}
                              onClick={async () => {
                                try {
                                  toast.loading("Generating PDF…", { id: `arcpt-${p.id}` });
                                  await downloadReceiptPDF(p, {});
                                  toast.success("Receipt downloaded", { id: `arcpt-${p.id}` });
                                } catch {
                                  toast.error("Could not generate PDF", { id: `arcpt-${p.id}` });
                                }
                              }}
                            >
                              <Download className="w-3 h-3 mr-1" /> A4 PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {payments.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground text-sm">No payments yet.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            <TabsContent value="tickets" className="mt-5">
              <Card>
                <ul className="divide-y divide-border">
                  {tickets.map(t => (
                    <li key={t.id} className="p-4 flex items-start justify-between gap-4">
                      <div>
                        <div className="font-semibold">{t.subject}</div>
                        <div className="text-xs text-muted-foreground">{t.category} • {t.user_name}</div>
                        <div className="text-sm mt-1">{t.description}</div>
                      </div>
                      <Select value={t.status} onValueChange={async (v) => {
                        try { await api.patch(`/tickets/${t.id}?status=${encodeURIComponent(v)}`); toast.success("Updated"); load(); }
                        catch { toast.error("Failed"); }
                      }}>
                        <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Open", "Assigned", "Resolved"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </li>
                  ))}
                  {tickets.length === 0 && <li className="p-12 text-center text-sm text-muted-foreground">No tickets.</li>}
                </ul>
              </Card>
            </TabsContent>

            <TabsContent value="gallery" className="mt-5">
              <GalleryAdmin />
            </TabsContent>

            <TabsContent value="staff" className="mt-5">
              <StaffAdmin />
            </TabsContent>

            <TabsContent value="claims" className="mt-5">
              <ClaimsAdmin />
            </TabsContent>

            <TabsContent value="feedback" className="mt-5">
              <FeedbackAdmin />
            </TabsContent>

            <TabsContent value="security" className="mt-5">
              {allowSuper && <SecurityTab onStatusChange={setTwofaEnabled} />}
            </TabsContent>

            <TabsContent value="settings" className="mt-5">
              {allowSuper && <SettingsTab />}
            </TabsContent>

            <TabsContent value="circulars" className="mt-5">
              <CircularsAdmin />
            </TabsContent>

            <TabsContent value="events" className="mt-5">
              <EventsAdmin />
            </TabsContent>

            <TabsContent value="charters" className="mt-5">
              <CharterUsageTab />
            </TabsContent>

            <TabsContent value="charter-studio" className="mt-5">
              {allowSuper && <CharterStudioTab />}
            </TabsContent>

            <TabsContent value="insurance" className="mt-5">
              {isAdminRole(role) && <AdminInsuranceTab role={role} district={user?.district || ""} />}
            </TabsContent>

            <TabsContent value="districts" className="mt-5">
              <Card className="p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <h2 className="text-lg font-bold">Paid &amp; Active Members by District</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Only members with payment status <span className="font-medium text-foreground">Paid</span> and membership status <span className="font-medium text-foreground">Active</span> are counted here.</p>
                  </div>
                  <div className="flex gap-2 flex-wrap items-center">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Summary</span>
                    <Button variant="outline" size="sm" onClick={() => exportDistrictsCSV(stats)} data-testid="admin-districts-export-csv">
                      <Download className="w-4 h-4 mr-1.5" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportDistrictsXLSX(stats)} data-testid="admin-districts-export-xlsx">
                      <Download className="w-4 h-4 mr-1.5" /> Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportDistrictsPDF(stats)} data-testid="admin-districts-export-pdf">
                      <Download className="w-4 h-4 mr-1.5" /> PDF
                    </Button>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold ml-2">Detailed</span>
                    <Button variant="outline" size="sm" onClick={() => exportDistrictDetails("csv")} data-testid="admin-districts-export-details-csv">
                      <Download className="w-4 h-4 mr-1.5" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportDistrictDetails("xlsx")} data-testid="admin-districts-export-details-xlsx">
                      <Download className="w-4 h-4 mr-1.5" /> Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportDistrictDetails("pdf")} data-testid="admin-districts-export-details-pdf">
                      <Download className="w-4 h-4 mr-1.5" /> PDF
                    </Button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {stats && Object.entries(stats.by_district_paid_active || stats.by_district || {}).map(([d, count]) => (
                    <button
                      type="button"
                      key={d}
                      onClick={() => setDistrictDetail(d)}
                      className="rounded-lg border border-border p-4 bg-card text-left cursor-pointer transition-[border-color,box-shadow,transform] hover:border-brand-blue/50 hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
                      data-testid={`district-card-${d.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      <div className="text-xs text-muted-foreground">{d}</div>
                      <div className="text-2xl font-black mt-1 text-brand-blue">{count}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid &amp; Active · View details</div>
                    </button>
                  ))}
                </div>
                {stats && (
                  <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Total across all districts</span>
                    <span className="font-black text-brand-blue text-lg">
                      {Object.values(stats.by_district_paid_active || {}).reduce((a, b) => a + b, 0)}
                    </span>
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="conference" className="mt-5">
              <ConferenceAdminPanel user={user} />
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
      <DistrictMembersDialog
        district={districtDetail}
        open={!!districtDetail}
        onOpenChange={(open) => !open && setDistrictDetail(null)}
      />
      <Footer />

      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />

      <TakePaymentDialog
        open={!!takePaymentTarget}
        onOpenChange={(o) => !o && setTakePaymentTarget(null)}
        member={takePaymentTarget?.member || null}
        defaultMode={takePaymentTarget?.mode || "offline"}
        onSuccess={() => { setTakePaymentTarget(null); load(); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this member?</AlertDialogTitle>
            <AlertDialogDescription>
              Only unpaid or pending test registrations can be deleted. Paid/active members are protected here; use Suspend instead so receipts and membership history remain available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-member-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              data-testid="admin-delete-member-confirm"
              onClick={confirmDelete}
            >
              Yes, delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, onClick, testId }) {
  return (
    <Card className={`p-5 hover-lift ${onClick ? "cursor-pointer" : ""}`} onClick={onClick} data-testid={testId}>
      <Icon className={`w-5 h-5 ${color} mb-2`} />
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
      {onClick && <div className="text-[10px] text-brand-blue font-semibold mt-1">Click to view list →</div>}
    </Card>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setShow(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next) { toast.error("Fill both password fields"); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next === current) { toast.error("New password must be different from the current one"); return; }
    try {
      setBusy(true);
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Rotate your account password. You&apos;ll stay signed in on this device after the change.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Current Password</Label>
            <Input
              type={show ? "text" : "password"}
              value={current}
              onChange={e => setCurrent(e.target.value)}
              autoComplete="current-password"
              data-testid="change-pw-current"
            />
          </div>
          <div>
            <Label>New Password <span className="text-xs text-muted-foreground">(min 8 chars)</span></Label>
            <Input
              type={show ? "text" : "password"}
              value={next}
              onChange={e => setNext(e.target.value)}
              autoComplete="new-password"
              data-testid="change-pw-new"
            />
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input
              type={show ? "text" : "password"}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="change-pw-confirm"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground select-none">
            <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} data-testid="change-pw-show" />
            Show passwords
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="change-pw-cancel">Cancel</Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-brand-blue text-white hover:bg-brand-blue/90"
              data-testid="change-pw-submit"
            >
              {busy ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SortableHead({ label, sortKey, state, onSort, align = "left", testid }) {
  const active = state?.key === sortKey;
  const Icon = !active ? ArrowUpDown : state.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <button
        type="button"
        onClick={() => onSort(toggleSort(state, sortKey))}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider hover:text-brand-blue transition-colors ${active ? "text-brand-blue" : "text-muted-foreground"} ${align === "right" ? "justify-end w-full" : ""}`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
}

function StatusBadge({ s }) {
  const map = {
    pending_payment: "bg-yellow-500/15 text-yellow-700",
    pending_review: "bg-blue-500/15 text-blue-700",
    active: "bg-brand-green/15 text-brand-green",
    suspended: "bg-red-500/15 text-red-600",
    rejected: "bg-red-500/15 text-red-600",
  };
  return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${map[s] || "bg-muted"}`}>{(s || "").replace("_", " ")}</span>;
}

function GalleryAdmin() {  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ image_url: "", caption: "", category: "General", date: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);

  const load = () => api.get("/gallery").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Image only"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Max 4MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, image_url: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.image_url) { toast.error("Upload an image or paste URL"); return; }
    try {
      setBusy(true);
      await api.post("/gallery", form);
      toast.success("Photo added to gallery");
      setOpen(false);
      setForm({ image_url: "", caption: "", category: "General", date: "" });
      load();
    } catch { toast.error("Failed to add"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card>
      <div className="p-4 flex items-center justify-between border-b border-border">
        <div className="text-sm font-semibold">{items.length} photo{items.length !== 1 ? "s" : ""}</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue/90" data-testid="gallery-add-btn">
              <ImagePlus className="w-4 h-4 mr-1.5" /> Add Photo
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Gallery Photo</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Upload Image</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
                <div className="flex items-center gap-3">
                  <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                    <ImagePlus className="w-4 h-4 mr-1.5" /> Choose File
                  </Button>
                  {form.image_url && <img src={form.image_url} alt="" className="w-14 h-14 rounded-md object-cover ring-1 ring-border" />}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">JPG/PNG, max 4MB. Or paste a URL below.</p>
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input value={form.image_url.startsWith("data:") ? "" : form.image_url}
                  onChange={(e) => setForm(f => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Input value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} placeholder="Government Meetings" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Caption</Label>
                <Textarea rows={3} value={form.caption} onChange={(e) => setForm(f => ({ ...f, caption: e.target.value }))} placeholder="Describe the event or moment..." />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="bg-brand-blue text-white">
                  {busy ? "Adding…" : "Add Photo"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map(g => (
          <div key={g.id} className="relative group rounded-lg overflow-hidden border border-border">
            <img src={g.image_url} alt={g.caption} className="w-full h-32 object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Button size="sm" variant="destructive" onClick={() => remove(g.id)} data-testid={`gallery-delete-${g.id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            </div>
            <div className="p-2 text-[10px] text-muted-foreground truncate">{g.category} • {g.date}</div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-12 text-sm text-muted-foreground">No photos yet.</div>}
      </div>
    </Card>
  );
}

const CIRCULAR_CATEGORIES = ["Government Order", "Association Circular", "Download"];

function CircularsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, object = editing
  const empty = { title: "", category: "Government Order", summary: "", date: "", file_url: "", visibility: "public" };
  const [form, setForm] = useState(empty);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);

  const load = () => api.get("/circulars").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setFileName("");
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      title: c.title || "",
      category: c.category || "Government Order",
      summary: c.summary || "",
      date: c.date || "",
      file_url: c.file_url || "",
      visibility: c.visibility || "public",
    });
    setFileName(c.file_url ? "Existing attachment" : "");
    setOpen(true);
  };

  const onFile = (file) => {
    if (!file) return;
    const ok = file.type === "application/pdf" || file.type.startsWith("image/");
    if (!ok) { toast.error("PDF or JPG/PNG only"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(f => ({ ...f, file_url: ev.target.result }));
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.summary || !form.date) { toast.error("Fill title, summary and date"); return; }
    try {
      setBusy(true);
      if (editing) {
        await api.patch(`/circulars/${editing.id}`, form);
        toast.success("Circular updated");
      } else {
        await api.post("/circulars", form);
        toast.success("Circular uploaded");
      }
      setOpen(false);
      setForm(empty);
      setFileName("");
      setEditing(null);
      load();
    } catch { toast.error(editing ? "Update failed" : "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this circular?")) return;
    try { await api.delete(`/circulars/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card>
      <div className="p-4 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-brand-blue" />
          <div className="text-sm font-semibold">{items.length} circular{items.length !== 1 ? "s" : ""}</div>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); setFileName(""); } }}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue/90" data-testid="circular-add-btn" onClick={openNew}>
              <Plus className="w-4 h-4 mr-1" /> Upload Circular
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Circular / Order" : "Upload Circular / Order"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required data-testid="circular-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="circular-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CIRCULAR_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required data-testid="circular-date" />
                </div>
              </div>
              <div>
                <Label>Visibility</Label>
                <Select value={form.visibility} onValueChange={v => setForm(f => ({ ...f, visibility: v }))}>
                  <SelectTrigger data-testid="circular-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public — show on homepage</SelectItem>
                    <SelectItem value="private">Private — only Technical Assistants (members)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Public circulars appear on the homepage and to anonymous visitors. Private circulars are visible only after login.
                </p>
              </div>
              <div>
                <Label>Summary</Label>
                <Textarea rows={3} value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} required data-testid="circular-summary" />
              </div>
              <div>
                <Label>Attachment (PDF / JPG / PNG)</Label>
                <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])} data-testid="circular-file-input" />
                <div className="flex items-center gap-3">
                  <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-1.5" /> {editing && form.file_url ? "Replace File" : "Choose File"}
                  </Button>
                  {fileName && <div className="text-xs text-muted-foreground truncate max-w-[220px]">{fileName}</div>}
                  {form.file_url && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setForm(f => ({ ...f, file_url: "" })); setFileName(""); }}>
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">PDF or image, max 8MB. Optional.</p>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="bg-brand-blue text-white" data-testid="circular-submit-btn">
                  {busy ? (editing ? "Saving…" : "Uploading…") : (editing ? "Save Changes" : "Upload")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <ul className="divide-y divide-border">
        {items.length === 0 && <li className="p-12 text-center text-sm text-muted-foreground">No circulars yet.</li>}
        {items
          .slice()
          .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
          .map(c => (
          <li key={c.id} className="p-4 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="secondary" className="text-[10px]">{c.category}</Badge>
                <Badge
                  variant="outline"
                  className={`text-[10px] ${(c.visibility || "public") === "public" ? "border-brand-green/40 text-brand-green" : "border-brand-orange/40 text-brand-orange"}`}
                >
                  {(c.visibility || "public") === "public" ? "Public" : "Private (TA only)"}
                </Badge>
                <span className="text-xs text-muted-foreground">{formatDateDMY(c.date)}</span>
              </div>
              <div className="font-semibold">{c.title}</div>
              <div className="text-sm text-muted-foreground line-clamp-2">{c.summary}</div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {c.file_url && (
                <a href={c.file_url} target="_blank" rel="noreferrer" className="text-xs text-brand-blue hover:underline inline-flex items-center gap-1">
                  <Download className="w-3 h-3" /> View
                </a>
              )}
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`circular-edit-${c.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="text-destructive hover:text-destructive">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const EVENT_TYPES = ["Training", "Workshop", "Conference", "District Meeting"];

function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "Training", description: "", date: "", location: "", district: "", seats: 100 });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/events").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.location) { toast.error("Fill title, date and location"); return; }
    try {
      setBusy(true);
      await api.post("/events", { ...form, seats: Number(form.seats) || 100, district: form.district || null });
      toast.success("Event added");
      setOpen(false);
      setForm({ title: "", type: "Training", description: "", date: "", location: "", district: "", seats: 100 });
      load();
    } catch { toast.error("Failed to add"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card>
      <div className="p-4 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-brand-green" />
          <div className="text-sm font-semibold">{items.length} event{items.length !== 1 ? "s" : ""}</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-brand-green text-white hover:bg-brand-green/90" data-testid="event-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Event
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Upcoming Event / Training</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required data-testid="event-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                    <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required data-testid="event-date" />
                </div>
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Venue address" required data-testid="event-location" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>District (optional)</Label>
                  <Input value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))} placeholder="Ernakulam" data-testid="event-district" />
                </div>
                <div>
                  <Label>Seats</Label>
                  <Input type="number" min="1" value={form.seats} onChange={e => setForm(f => ({ ...f, seats: e.target.value }))} data-testid="event-seats" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="event-description" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="bg-brand-green text-white" data-testid="event-submit-btn">
                  {busy ? "Adding…" : "Add Event"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <ul className="divide-y divide-border">
        {items.length === 0 && <li className="p-12 text-center text-sm text-muted-foreground">No events yet.</li>}
        {items.map(e => (
          <li key={e.id} className="p-4 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px]">{e.type}</Badge>
                <span className="text-xs text-muted-foreground">{e.date}</span>
              </div>
              <div className="font-semibold">{e.title}</div>
              <div className="text-xs text-muted-foreground">{e.location}{e.district ? ` • ${e.district}` : ""}</div>
              {e.description && <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{e.description}</div>}
              <div className="text-[10px] text-muted-foreground mt-1">
                {(e.registrations?.length || 0)} / {e.seats} registered
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(e.id)} className="text-destructive hover:text-destructive">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}


const STAFF_ROLES = [
  { value: "state_president", label: "State President" },
  { value: "state_secretary", label: "State Secretary" },
  { value: "state_treasurer", label: "State Treasurer" },
  { value: "district_president", label: "District President" },
  { value: "district_secretary", label: "District Secretary" },
  { value: "district_treasurer", label: "District Treasurer" },
];
const DISTRICT_ROLE_VALUES = new Set(["district_president", "district_secretary", "district_treasurer"]);

function StaffAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { email: "", password: "", full_name: "", role: "state_treasurer", mobile: "", district: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/staff").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ email: s.email, password: "", full_name: s.full_name || "", role: s.role, mobile: s.mobile || "", district: s.district || "" });
    setOpen(true);
  };

  const isDistrictRole = DISTRICT_ROLE_VALUES.has(form.role);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.role) { toast.error("Fill name and role"); return; }
    if (!editing && !form.email) { toast.error("Email or username required"); return; }
    if (form.password && form.password.length < 6) { toast.error("Password min 6 chars"); return; }
    if (isDistrictRole && !form.district) { toast.error("Select a district for district-level roles"); return; }
    try {
      setBusy(true);
      const payload = {
        full_name: form.full_name,
        role: form.role,
        mobile: form.mobile || null,
      };
      if (isDistrictRole) payload.district = form.district;
      if (editing) {
        payload.email = form.email;
        if (form.password) payload.password = form.password;
        const { data } = await api.patch(`/admin/staff/${editing.id}`, payload);
        toast.success(data?.merged ? "Accounts merged and staff updated" : "Staff updated");
      } else {
        const { data } = await api.post("/admin/staff", { ...payload, email: form.email, password: form.password || undefined });
        toast.success(data?.linked_existing ? "Existing member linked to staff role" : "Staff created");
      }
      setOpen(false); setEditing(null); setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove staff access for ${s.email}? If this login belongs to a paid member, the member account and receipts will be preserved.`)) return;
    try {
      const { data } = await api.delete(`/admin/staff/${s.id}`);
      toast.success(data?.removed_staff_role ? "Staff role removed; member and receipts preserved" : "Staff-only account deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card>
      <div className="p-4 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-brand-blue" />
          <div className="text-sm font-semibold">{items.length} staff account{items.length !== 1 ? "s" : ""}</div>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue/90" onClick={openNew} data-testid="staff-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Staff Account" : "Create Staff Account"}</DialogTitle>
              <DialogDescription>
                {editing ? "Update this staff member's email/login ID, role, district, mobile or reset their password. Entering an existing member email merges the two accounts safely." : "Add a State- or District-level office bearer. Use an existing member's email to link that account; district roles are capped at 14 seats per district."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Full Name</Label>
                <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required data-testid="staff-name" />
              </div>
              <div>
                <Label>Email or Username</Label>
                <Input
                  type="text"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                  placeholder="e.g. secretary or secretary@kelta.co.in"
                  data-testid="staff-email"
                />
                {editing
                  ? <p className="text-[10px] text-muted-foreground mt-1">Super Admin can change this to an existing member email; the duplicate accounts will be merged.</p>
                  : <p className="text-[10px] text-muted-foreground mt-1">Use a real member email to link that account, or create a new username login. Password is needed only for a new login.</p>}
              </div>
              <div>
                <Label>{editing ? "New Password (leave blank to keep current)" : "Password (leave blank when linking an existing member)"}</Label>
                <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={editing ? "•••••• (unchanged)" : ""} data-testid="staff-password" />
              </div>
              <div>
                <Label>Mobile Number</Label>
                <Input
                  type="tel"
                  value={form.mobile}
                  onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
                  placeholder="+91 98xxx xxxxx"
                  data-testid="staff-mobile"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v, district: DISTRICT_ROLE_VALUES.has(v) ? f.district : "" }))}>
                  <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAFF_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  <strong>State</strong> roles have Kerala-wide access. <strong>District</strong> roles are scoped to a single district (max 14 seats per district).
                </p>
              </div>
              {isDistrictRole && (
                <div>
                  <Label>District</Label>
                  <Select value={form.district} onValueChange={v => setForm(f => ({ ...f, district: v }))}>
                    <SelectTrigger data-testid="staff-district"><SelectValue placeholder="Select district" /></SelectTrigger>
                    <SelectContent>
                      {DISTRICTS.map(d => <SelectItem key={d.slug} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={busy} className="bg-brand-blue text-white" data-testid="staff-submit-btn">
                  {busy ? (editing ? "Saving…" : "Creating…") : (editing ? "Save Changes" : "Create Staff")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <ul className="divide-y divide-border">
        {items.length === 0 && <li className="p-12 text-center text-sm text-muted-foreground">No staff accounts yet. Create one to share access.</li>}
        {items.map(s => (
          <li key={s.id} className="p-4 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge
                  variant="outline"
                  className={`text-[10px] ${
                    s.role === "admin" ? "border-brand-orange/50 text-brand-orange"
                    : s.role === "state_president" ? "border-brand-blue/50 text-brand-blue"
                    : s.role === "state_treasurer" ? "border-brand-green/50 text-brand-green"
                    : s.role?.startsWith("district_") ? "border-purple-500/50 text-purple-600"
                    : "border-purple-500/50 text-purple-600"
                  }`}
                >
                  {ROLE_LABELS[s.role] || s.role}
                </Badge>
                {s.district && (
                  <Badge variant="secondary" className="text-[10px]" data-testid={`staff-district-badge-${s.id}`}>
                    {s.district}
                  </Badge>
                )}
              </div>
              <div className="font-semibold">{s.full_name}</div>
              <div className="text-xs text-muted-foreground">{s.email}{s.mobile ? ` • ${s.mobile}` : ""}</div>
            </div>
            {s.role !== "admin" && (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`staff-edit-${s.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s)} className="text-destructive hover:text-destructive">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}


const CLAIM_STATUS = {
  pending: { label: "Pending", cls: "bg-yellow-500/15 text-yellow-700" },
  approved: { label: "Approved", cls: "bg-blue-500/15 text-blue-700" },
  paid: { label: "Paid", cls: "bg-brand-green/15 text-brand-green" },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-600" },
};

function ClaimsAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [actionOn, setActionOn] = useState(null); // { claim, kind }
  const [note, setNote] = useState("");
  const [ref, setRef] = useState("");

  const load = () => {
    const url = filter === "all" ? "/expense-claims" : `/expense-claims?status=${filter}`;
    api.get(url).then(r => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const totals = items.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + Number(c.amount || 0);
    return acc;
  }, {});

  const confirmAction = async () => {
    if (!actionOn) return;
    try {
      await api.patch(`/expense-claims/${actionOn.claim.id}/action`, {
        action: actionOn.kind,
        note,
        payment_reference: ref,
      });
      toast.success("Claim updated");
      setActionOn(null); setNote(""); setRef("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card>
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-b border-border">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-brand-blue" />
            <div className="text-sm font-semibold">{items.length} claim{items.length !== 1 ? "s" : ""}</div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <TotalPill label="Pending" v={totals.pending || 0} cls="text-yellow-700" />
            <TotalPill label="Approved" v={totals.approved || 0} cls="text-blue-700" />
            <TotalPill label="Paid" v={totals.paid || 0} cls="text-brand-green" />
          </div>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <ul className="divide-y divide-border">
        {items.length === 0 && <li className="p-12 text-center text-sm text-muted-foreground">No claims to review.</li>}
        {items.map(c => {
          const s = CLAIM_STATUS[c.status] || CLAIM_STATUS.pending;
          return (
            <li key={c.id} className="p-4 grid gap-3 lg:grid-cols-[1fr_auto]">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${s.cls}`}>{s.label}</span>
                  <Badge variant="outline" className="text-[10px]">{c.category}</Badge>
                  <span className="text-xs text-muted-foreground">{formatDateDMY(c.date_of_expense)}</span>
                </div>
                <div className="font-semibold">{c.description}</div>
                <div className="text-xs text-muted-foreground">
                  <strong>{c.full_name}</strong> · {c.committee_position} · {c.district} · {c.local_body_name} ({c.local_body_type})
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {c.phone} · {c.email} · Paid via <strong>{c.payment_method}</strong>
                </div>
                {c.more_details && <div className="text-xs mt-1">Notes: {c.more_details}</div>}
                <div className="text-xs text-muted-foreground mt-2 grid sm:grid-cols-2 gap-x-4">
                  {c.upi_id && <div>UPI: <span className="font-mono">{c.upi_id}</span></div>}
                  {c.account_number && <div>A/C: <span className="font-mono">{c.account_number}</span> ({c.bank_name})</div>}
                  {c.ifsc_code && <div>IFSC: <span className="font-mono">{c.ifsc_code}</span></div>}
                  {c.account_holder_name && <div>Holder: {c.account_holder_name}</div>}
                </div>
                {c.note && <div className="text-xs text-muted-foreground mt-1 italic">Reviewer note: {c.note}</div>}
                {c.payment_reference && <div className="text-xs text-brand-green mt-1">Payment ref: {c.payment_reference}</div>}
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="text-right">
                  <div className="text-2xl font-black font-mono">₹{Number(c.amount).toLocaleString("en-IN")}</div>
                </div>
                {c.bill_url && (
                  <Button size="sm" variant="outline" onClick={() => setViewing(c)}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Bill
                  </Button>
                )}
                <div className="flex gap-1 flex-wrap justify-end">
                  {c.status === "pending" && (
                    <>
                      <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue/90" onClick={() => setActionOn({ claim: c, kind: "approve" })}>Approve</Button>
                      <Button size="sm" variant="destructive" onClick={() => setActionOn({ claim: c, kind: "reject" })}>Reject</Button>
                    </>
                  )}
                  {c.status === "approved" && (
                    <Button size="sm" className="bg-brand-green text-white hover:bg-brand-green/90" onClick={() => setActionOn({ claim: c, kind: "mark_paid" })}>Mark as Paid</Button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <FileViewer
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing ? `Bill — ${viewing.description}` : ""}
        url={viewing?.bill_url}
      />

      <Dialog open={!!actionOn} onOpenChange={(o) => { if (!o) { setActionOn(null); setNote(""); setRef(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionOn?.kind === "approve" && "Approve Claim"}
              {actionOn?.kind === "reject" && "Reject Claim"}
              {actionOn?.kind === "mark_paid" && "Mark Claim as Paid"}
            </DialogTitle>
          </DialogHeader>
          {actionOn && (
            <div className="space-y-3">
              <div className="text-sm rounded-md bg-muted/40 p-3">
                <div className="font-semibold">{actionOn.claim.full_name} · ₹{Number(actionOn.claim.amount).toLocaleString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">{actionOn.claim.description}</div>
              </div>
              <div>
                <Label>Note (optional, visible to member)</Label>
                <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} />
              </div>
              {actionOn.kind === "mark_paid" && (
                <div>
                  <Label>Payment Reference (UTR / Transaction ID)</Label>
                  <Input value={ref} onChange={e => setRef(e.target.value)} placeholder="e.g., UPI-123456789" />
                </div>
              )}
              <DialogFooter>
                <Button onClick={confirmAction} className={
                  actionOn.kind === "reject" ? "bg-destructive text-white hover:bg-destructive/90"
                  : actionOn.kind === "mark_paid" ? "bg-brand-green text-white hover:bg-brand-green/90"
                  : "bg-brand-blue text-white hover:bg-brand-blue/90"
                }>Confirm</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TotalPill({ label, v, cls }) {
  return (
    <div className={`flex items-baseline gap-1 ${cls}`}>
      <IndianRupeeIcon className="w-3 h-3" />
      <span className="font-mono font-bold">{Number(v).toLocaleString("en-IN")}</span>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
}

function exportFeedbackCsv(items) {
  if (!items || items.length === 0) return;
  const cols = ["Date", "Category", "Status", "Name", "Email", "Phone", "Department", "Subject", "Message", "Admin Notes"];
  const rows = items.map(f => [
    (f.created_at || "").slice(0, 10),
    f.category || "",
    f.status || "",
    f.name || "",
    f.email || "",
    f.phone || "",
    f.department || "",
    f.subject || "",
    (f.message || "").replace(/\r?\n/g, " "),
    f.admin_notes || "",
  ]);
  const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [cols, ...rows].map(r => r.map(escape).join(",")).join("\r\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `kelta_feedback_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}


const FB_STATUS = {
  new: { label: "New", cls: "bg-brand-orange/15 text-brand-orange" },
  reviewed: { label: "Reviewed", cls: "bg-blue-500/15 text-blue-700" },
  actioned: { label: "Actioned", cls: "bg-brand-green/15 text-brand-green" },
  archived: { label: "Archived", cls: "bg-muted text-muted-foreground" },
};
const FB_STATUS_KEYS = Object.keys(FB_STATUS);

function FeedbackAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    const url = filter === "all" ? "/feedback" : `/feedback?status=${filter}`;
    api.get(url).then(r => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try { await api.patch(`/feedback/${id}`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    try { await api.delete(`/feedback/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card>
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-b border-border">
        <div className="text-sm font-semibold">
          {items.length} feedback item{items.length !== 1 ? "s" : ""}
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {FB_STATUS_KEYS.map(k => <SelectItem key={k} value={k}>{FB_STATUS[k].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => exportFeedbackCsv(items)} data-testid="fb-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>
      <ul className="divide-y divide-border">
        {items.length === 0 && <li className="p-12 text-center text-sm text-muted-foreground">No feedback submissions yet.</li>}
        {items.map(f => {
          const s = FB_STATUS[f.status] || FB_STATUS.new;
          return (
            <li key={f.id} className="p-4 grid gap-3 lg:grid-cols-[1fr_auto]">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${s.cls}`}>{s.label}</span>
                  <Badge variant="outline" className="text-[10px]">{f.category}</Badge>
                  <span className="text-xs text-muted-foreground">{formatDateDMY(f.created_at)}</span>
                </div>
                <div className="font-semibold">{f.subject}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  <strong>{f.name}</strong>
                  {f.email && <> · {f.email}</>}
                  {f.phone && <> · {f.phone}</>}
                  {f.department && <> · {f.department}</>}
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap">{f.message}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Select
                  value={f.status || "new"}
                  onValueChange={(v) => updateStatus(f.id, v)}
                  disabled={busyId === f.id}
                >
                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FB_STATUS_KEYS.map(k => <SelectItem key={k} value={k}>{FB_STATUS[k].label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="text-destructive hover:text-destructive">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

